import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { Plus, X, ChevronUp, ChevronDown, Loader2, AlertTriangle } from 'lucide-react';
import { INPUT, LABEL, STRUCTURAL_TYPES } from '../../lib/formConstants';
import { STEP_CATALOGUE, STEP_BY_OP, STEP_GROUPS } from '../../lib/fieldTransforms';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ON_EMPTY_OPTIONS = [
    ['omit', 'Send nothing (leave the target field untouched)'],
    ['source', 'Use the source value as-is'],
    ['default', 'Use a fallback value'],
];

/**
 * DeriveEditor — configures `config.derive` on an always-hidden field:
 * a source field, an ordered transform pipeline, and an empty-result policy.
 *
 * The live preview calls POST /api/forms/transform/preview rather than the
 * local JS engine, because the SERVER is authoritative at submit time and the
 * two regex flavours can disagree. What the author sees here is what
 * production computes.
 *
 * Props:
 *   field       — the hidden field being configured
 *   allFields   — flattened list of all form fields
 *   forwarders  — form forwarders, for the late-source warning
 *   steps       — form steps, for the late-source warning
 *   onUpdate    — FieldInspector's partial-merge callback
 */
export default function DeriveEditor({ field, allFields = [], forwarders = [], steps = [], onUpdate }) {
    const { getAuthHeader } = useAuth();
    const derive = field.config?.derive || null;
    const pipeline = useMemo(() => derive?.pipeline || [], [derive]);

    const [showPicker, setShowPicker] = useState(false);
    const [pickerQuery, setPickerQuery] = useState('');
    const [sample, setSample] = useState('');
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const debounceRef = useRef(null);

    // ── Source candidates ───────────────────────────────────────────────────
    // Exclude self, structural elements, and any field that (transitively)
    // derives from this one — picking it would create a cycle, which the
    // backend rejects at save time anyway.
    const sourceCandidates = useMemo(() => {
        const derivesFrom = (f, targetId, seen = new Set()) => {
            const src = f?.config?.derive?.source_field_id;
            if (!src || seen.has(f.field_id)) return false;
            if (src === targetId) return true;
            seen.add(f.field_id);
            const next = allFields.find(x => x.field_id === src);
            return next ? derivesFrom(next, targetId, seen) : false;
        };
        return allFields.filter(f =>
            f.field_id !== field.field_id &&
            !STRUCTURAL_TYPES.includes(f.field_type) &&
            !derivesFrom(f, field.field_id)
        );
    }, [allFields, field.field_id]);

    const commit = useCallback((patch) => {
        onUpdate({
            config: {
                ...(field.config || {}),
                derive: {
                    source_field_id: '',
                    pipeline: [],
                    on_empty: 'omit',
                    default_value: '',
                    ...(field.config?.derive || {}),
                    ...patch,
                },
            },
        });
    }, [field.config, onUpdate]);

    const clearDerive = () => {
        const { derive: _drop, ...restConfig } = field.config || {};
        onUpdate({ config: Object.keys(restConfig).length ? restConfig : null });
    };

    const setStep = (idx, patch) =>
        commit({ pipeline: pipeline.map((s, i) => (i === idx ? { ...s, ...patch } : s)) });
    const removeStep = (idx) => commit({ pipeline: pipeline.filter((_, i) => i !== idx) });
    const moveStep = (idx, delta) => {
        const next = [...pipeline];
        const j = idx + delta;
        if (j < 0 || j >= next.length) return;
        [next[idx], next[j]] = [next[j], next[idx]];
        commit({ pipeline: next });
    };
    const addStep = (op) => {
        const def = STEP_BY_OP[op];
        const seed = { op };
        (def?.params || []).forEach(p => {
            if (p.default !== undefined) seed[p.key] = p.default;
        });
        commit({ pipeline: [...pipeline, seed] });
        setShowPicker(false);
        setPickerQuery('');
    };

    // ── Late-source warning ─────────────────────────────────────────────────
    // A forwarder attached to step N fires when step N is submitted. If the
    // source field lives on a later step, that forwarder sends this field
    // empty. Real footgun on the per-step forwarder path.
    const lateSourceWarning = useMemo(() => {
        const srcId = derive?.source_field_id;
        if (!srcId || !steps.length || !forwarders.length) return null;

        const stepIndexOfField = (fid) => steps.findIndex(s =>
            JSON.stringify(s.fields || []).includes(`"${fid}"`));
        const srcStep = stepIndexOfField(srcId);
        if (srcStep < 0) return null;

        // A forwarder fires after the step whose next_step_id lists its node id.
        const mapsThisField = (fwd) =>
            (fwd.ghl_field_mappings || []).some(m => m.form_field_id === field.field_id);
        for (const fwd of forwarders) {
            if (!fwd.step_id || !mapsThisField(fwd)) continue;
            const hostStep = steps.findIndex(s => {
                const nxt = s.next_step_id;
                const arr = Array.isArray(nxt) ? nxt : (nxt ? [nxt] : []);
                return arr.includes(fwd.step_id);
            });
            if (hostStep >= 0 && hostStep < srcStep) {
                return {
                    forwarder: fwd.name || fwd.step_id,
                    forwarderStep: hostStep + 1,
                    sourceStep: srcStep + 1,
                };
            }
        }
        return null;
    }, [derive, steps, forwarders, field.field_id]);

    // ── Server preview (debounced) ──────────────────────────────────────────
    useEffect(() => {
        if (!sample || !pipeline.length) { setPreview(null); return undefined; }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setPreviewing(true);
            try {
                const res = await axios.post(
                    `${API}/forms/transform/preview`,
                    { input: sample, pipeline },
                    { headers: getAuthHeader() },
                );
                setPreview(res.data);
            } catch (err) {
                setPreview({ error: err.response?.data?.detail || 'Preview failed' });
            } finally {
                setPreviewing(false);
            }
        }, 300);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [sample, pipeline, getAuthHeader]);

    // ── Not configured yet ──────────────────────────────────────────────────
    if (!derive) {
        return (
            <div className="rounded-lg border border-dashed border-stone-200 p-3 text-center">
                <p className="text-[11px] text-stone-500 mb-2">
                    Give this field a value derived from another field.
                </p>
                <button
                    type="button"
                    onClick={() => commit({})}
                    disabled={!sourceCandidates.length}
                    className="text-xs font-medium text-stone-700 border border-stone-200 rounded-lg px-2.5 py-1.5 hover:border-stone-400 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {sourceCandidates.length ? 'Set up a derived value' : 'No other fields to derive from'}
                </button>
            </div>
        );
    }

    const filteredCatalogue = STEP_CATALOGUE.filter(s =>
        !pickerQuery ||
        s.label.toLowerCase().includes(pickerQuery.toLowerCase()) ||
        s.op.includes(pickerQuery.toLowerCase()));

    return (
        <div className="space-y-3 rounded-lg border border-stone-200 p-2.5">
            {/* Source */}
            <div>
                <div className="flex items-center justify-between mb-1">
                    <label className={`${LABEL} mb-0`}>Source field</label>
                    <button
                        type="button"
                        onClick={clearDerive}
                        className="text-[10px] text-stone-400 hover:text-red-500"
                    >
                        Remove
                    </button>
                </div>
                <select
                    value={derive.source_field_id || ''}
                    onChange={e => commit({ source_field_id: e.target.value })}
                    className={INPUT}
                >
                    <option value="">Select a field…</option>
                    {sourceCandidates.map(f => (
                        <option key={f.field_id} value={f.field_id}>
                            {f.label || 'Untitled'} ({f.field_type})
                        </option>
                    ))}
                </select>
            </div>

            {lateSourceWarning && (
                <div className="flex gap-1.5 rounded-lg bg-amber-50 border border-amber-200 p-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-[10px] text-amber-800 leading-relaxed">
                        The source is on page {lateSourceWarning.sourceStep}, but{' '}
                        <strong>{lateSourceWarning.forwarder}</strong> fires after page{' '}
                        {lateSourceWarning.forwarderStep} — it will send this field empty.
                        Move the source earlier, or map this field on a later forwarder too.
                    </p>
                </div>
            )}

            {/* Pipeline */}
            <div>
                <div className="flex items-center justify-between mb-1">
                    <label className={`${LABEL} mb-0`}>Transform steps</label>
                    <button
                        type="button"
                        onClick={() => setShowPicker(v => !v)}
                        className="flex items-center gap-0.5 text-[10px] text-stone-500 hover:text-stone-700"
                    >
                        <Plus className="w-3 h-3" /> Add step
                    </button>
                </div>

                {!pipeline.length && (
                    <p className="text-[10px] text-stone-400 py-1.5">
                        No steps — the source value is copied unchanged.
                    </p>
                )}

                <div className="space-y-1.5">
                    {pipeline.map((step, idx) => {
                        const def = STEP_BY_OP[step.op];
                        return (
                            <div key={idx} className="rounded-lg border border-stone-100 bg-stone-50/60 p-2">
                                <div className="flex items-center gap-1">
                                    <span className="text-[10px] font-medium text-stone-400 w-4">{idx + 1}.</span>
                                    <span className="text-xs text-stone-700 flex-1 truncate">
                                        {def?.label || step.op}
                                        {def?.advanced && (
                                            <span className="ml-1 text-[9px] text-amber-600">advanced</span>
                                        )}
                                        {!def && (
                                            <span className="ml-1 text-[9px] text-red-500">unknown step</span>
                                        )}
                                    </span>
                                    <button type="button" onClick={() => moveStep(idx, -1)} disabled={idx === 0}
                                        className="p-0.5 text-stone-400 hover:text-stone-700 disabled:opacity-25" title="Move up">
                                        <ChevronUp className="w-3.5 h-3.5" />
                                    </button>
                                    <button type="button" onClick={() => moveStep(idx, 1)} disabled={idx === pipeline.length - 1}
                                        className="p-0.5 text-stone-400 hover:text-stone-700 disabled:opacity-25" title="Move down">
                                        <ChevronDown className="w-3.5 h-3.5" />
                                    </button>
                                    <button type="button" onClick={() => removeStep(idx)}
                                        className="p-0.5 text-stone-400 hover:text-red-500" title="Remove step">
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>

                                {def?.hint && !def.params.length && (
                                    <p className="text-[9px] text-stone-400 ml-5 mt-0.5">{def.hint}</p>
                                )}

                                {/* to_number returns a number, so every later step is a
                                    silent no-op. The server rejects this on save — warn here
                                    so the author does not discover it via a 422. */}
                                {step.op === 'to_number' && idx !== pipeline.length - 1 && (
                                    <p className="text-[9px] text-red-600 ml-5 mt-0.5">
                                        “Convert to number” must be the last step — later steps
                                        will not run. Saving will be rejected.
                                    </p>
                                )}

                                {!!def?.params?.length && (
                                    <div className="ml-5 mt-1.5 space-y-1.5">
                                        {def.params.map(p => (
                                            <div key={p.key}>
                                                {p.type === 'boolean' ? (
                                                    <label className="flex items-center gap-1.5 text-[10px] text-stone-600">
                                                        <input type="checkbox" checked={!!step[p.key]}
                                                            onChange={e => setStep(idx, { [p.key]: e.target.checked })}
                                                            className="rounded border-stone-300" />
                                                        {p.label}
                                                    </label>
                                                ) : p.type === 'select' ? (
                                                    <select value={step[p.key] ?? p.default ?? ''}
                                                        onChange={e => setStep(idx, { [p.key]: e.target.value })}
                                                        className="w-full px-2 py-1 text-[11px] border border-stone-200 rounded-md bg-white">
                                                        {p.options.map(o => <option key={o} value={o}>{o}</option>)}
                                                    </select>
                                                ) : p.type === 'field' ? (
                                                    <select value={step[p.key] ?? ''}
                                                        onChange={e => setStep(idx, { [p.key]: e.target.value })}
                                                        className="w-full px-2 py-1 text-[11px] border border-stone-200 rounded-md bg-white">
                                                        <option value="">{p.label}…</option>
                                                        {sourceCandidates.map(f => (
                                                            <option key={f.field_id} value={f.field_id}>
                                                                {f.label || 'Untitled'}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input
                                                        type={p.type === 'number' ? 'number' : 'text'}
                                                        value={step[p.key] ?? ''}
                                                        onChange={e => setStep(idx, {
                                                            [p.key]: p.type === 'number'
                                                                ? (e.target.value === '' ? null : Number(e.target.value))
                                                                : e.target.value,
                                                        })}
                                                        placeholder={p.label}
                                                        className={`w-full px-2 py-1 text-[11px] border border-stone-200 rounded-md bg-white ${p.mono ? 'font-mono' : ''}`}
                                                    />
                                                )}
                                            </div>
                                        ))}
                                        {def.advanced && (
                                            <p className="text-[9px] text-amber-700 bg-amber-50 rounded px-1.5 py-1">
                                                Evaluated server-side. The preview below uses the same
                                                engine, so it is authoritative.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {showPicker && (
                    <div className="mt-1.5 rounded-lg border border-stone-200 bg-white shadow-sm max-h-64 overflow-y-auto">
                        <input
                            autoFocus value={pickerQuery} onChange={e => setPickerQuery(e.target.value)}
                            placeholder="Search steps…"
                            className="w-full px-2.5 py-2 text-xs border-b border-stone-100 focus:outline-none"
                        />
                        {STEP_GROUPS.map(group => {
                            const items = filteredCatalogue.filter(s => s.group === group);
                            if (!items.length) return null;
                            return (
                                <div key={group}>
                                    <div className="px-2.5 pt-2 pb-0.5 text-[9px] font-semibold uppercase tracking-wide text-stone-400">
                                        {group}
                                    </div>
                                    {items.map(s => (
                                        <button
                                            key={s.op} type="button" onClick={() => addStep(s.op)}
                                            className="w-full text-left px-2.5 py-1.5 hover:bg-stone-50"
                                        >
                                            <div className="text-xs text-stone-700">
                                                {s.label}
                                                {s.advanced && <span className="ml-1 text-[9px] text-amber-600">advanced</span>}
                                            </div>
                                            {s.hint && <div className="text-[9px] text-stone-400">{s.hint}</div>}
                                        </button>
                                    ))}
                                </div>
                            );
                        })}
                        {!filteredCatalogue.length && (
                            <p className="px-2.5 py-3 text-[11px] text-stone-400">No matching steps.</p>
                        )}
                    </div>
                )}
            </div>

            {/* Empty-result policy */}
            <div>
                <label className={LABEL}>If the result is empty</label>
                <select
                    value={derive.on_empty || 'omit'}
                    onChange={e => commit({ on_empty: e.target.value })}
                    className={INPUT}
                >
                    {ON_EMPTY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                {derive.on_empty === 'default' && (
                    <input
                        value={derive.default_value || ''}
                        onChange={e => commit({ default_value: e.target.value })}
                        placeholder="Fallback value…"
                        className={`${INPUT} mt-1.5`}
                    />
                )}
            </div>

            {/* Preview */}
            <div className="border-t border-stone-100 pt-2">
                <div className="flex items-center justify-between mb-1">
                    <label className={`${LABEL} mb-0`}>Preview</label>
                    <span className="text-[9px] text-stone-400">
                        {previewing ? <Loader2 className="w-3 h-3 animate-spin inline" /> : 'server engine'}
                    </span>
                </div>
                <input
                    value={sample}
                    onChange={e => setSample(e.target.value)}
                    placeholder="Test input, e.g. http://multiplai.tech"
                    className={INPUT}
                />
                {preview?.error && (
                    <p className="text-[10px] text-red-500 mt-1">{preview.error}</p>
                )}
                {preview && !preview.error && (
                    <div className="mt-1.5 rounded-lg bg-stone-50 border border-stone-100 p-2 space-y-0.5">
                        {preview.trace.map(t => (
                            <div key={t.index} className="flex gap-1.5 text-[10px]">
                                <span className="text-stone-400 w-3">{t.index}</span>
                                <span className="text-stone-500 flex-1 truncate">
                                    {STEP_BY_OP[t.op]?.label || t.op}
                                    {!t.known && <span className="text-red-500 ml-1">unknown</span>}
                                </span>
                                <span className="font-mono text-stone-700 truncate max-w-[45%]">
                                    {t.after === '' ? '(empty)' : String(t.after)}
                                </span>
                            </div>
                        ))}
                        <div className="flex gap-1.5 text-[10px] border-t border-stone-200 pt-1 mt-1">
                            <span className="text-stone-500 flex-1 font-medium">Result</span>
                            <span className={`font-mono font-medium ${preview.empty ? 'text-amber-600' : 'text-emerald-700'}`}>
                                {preview.empty ? '(empty)' : String(preview.result)}
                            </span>
                        </div>
                        {preview.empty && (
                            <p className="text-[9px] text-amber-700 pt-0.5">
                                {derive.on_empty === 'omit'
                                    ? 'Nothing will be sent for this field.'
                                    : derive.on_empty === 'source'
                                        ? 'The raw source value will be sent instead.'
                                        : `"${derive.default_value || ''}" will be sent instead.`}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
